// Explore-stats page: Royal Commissions since Federation (#221).
// Sourced from the `royal_commissions` index (Parliamentary Handbook API).
// Flagship: chronological, sortable listing of every RC with letters-patent
// and final-report dates, duration, a coarse topic bucket, and a link to the
// first archived report. Slices: by decade (count + cumulative), by topic,
// and the longest-running RCs.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine } from './charts';

export default function RoyalCommissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('royal-commissions');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Royal Commissions"
      description={`Every Royal Commission since Federation${
        charts.total ? ` (${charts.total})` : ''
      } — dates, duration, topic, and links to the archived reports. Topic is a coarse keyword heuristic from the title; initiating-PM attribution isn't in the source data yet.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="royal-commissions"
    >
      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Royal Commissions per decade</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Number established each decade (by letters-patent date), with the running total.
            </Typography>
            <StatLine data={charts.by_decade || []} xKey="decade"
                      keys={['count', 'cumulative']} height={320} />
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>By topic</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Coarse buckets derived from each RC's title (heuristic).
                </Typography>
                <StatBar data={charts.by_topic || []} xKey="topic" yKey="count"
                         layout="vertical" height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Longest-running</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Top 15 by days from letters patent to final report.
                </Typography>
                <StatBar data={charts.longest || []} xKey="title" yKey="days"
                         layout="vertical" height={300} />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </ExploreStatsPage>
  );
}
